import * as React from 'react';

import cn from 'classnames';

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot='card' className={cn('bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm', className)} {...props} />
  );
}

/**
 * Card - Container component for card UI.
 *
 * Accepts any props valid for a `div` element via `React.ComponentProps<'div'>`.
 * The `className` prop is merged with default card styles.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the root `div`.
 * @returns {JSX.Element} The rendered card container element.
 */

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-header'
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-2 py-1 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className
      )}
      {...props}
    />
  );
}

/**
 * CardHeader - Header area for the `Card` component.
 *
 * Forwards all `div` props and supports `className` for additional styling.
 * Typically used to render the title and optional action elements.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the header `div`.
 * @returns {JSX.Element} The rendered card header.
 */

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <h3 data-slot='card-title' className={cn('leading-none font-semibold', className)} {...props} />;
}

/**
 * CardTitle - Title element for use inside a `CardHeader`.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the title `div`.
 * @returns {JSX.Element} The rendered title element.
 */

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='card-description' className={cn('text-muted-foreground text-sm', className)} {...props} />;
}

/**
 * CardDescription - Subtitle/description text for a card.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the description `div`.
 * @returns {JSX.Element} The rendered description element.
 */

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='card-action' className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)} {...props} />;
}

/**
 * CardAction - Container for action elements (buttons, menus) placed in the header.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the action `div`.
 * @returns {JSX.Element} The rendered action container.
 */

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='card-content' className={cn('px-6', className)} {...props} />;
}

/**
 * CardContent - Main content area for a card.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the content `div`.
 * @returns {JSX.Element} The rendered content section.
 */

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='card-footer' className={cn('flex items-center px-6 [.border-t]:pt-6', className)} {...props} />;
}

/**
 * CardFooter - Footer area for a card, typically for actions or metadata.
 *
 * @param {React.ComponentProps<'div'>} props - Props forwarded to the footer `div`.
 * @returns {JSX.Element} The rendered footer element.
 */

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
